import {
  useSessionUsesTuniAuth,
  useTuniAuthMode,
  useTuniAuthStrict,
} from '#app/utils/env.ts'

type DisplayableUser = {
  username: string
  name?: string | null
  email?: string | null
}

/**
 * Hook that provides user display logic based on the authentication mode.
 *
 * When TUNI auth is enabled, username is an opaque identifier (SHIB_eppn)
 * that should not be shown in the UI. Email is used instead.
 *
 * When TUNI auth is disabled (standard login), username is used normally.
 */
export function useUserDisplay() {
  const authMode = useTuniAuthMode()
  const isStrictTuniAuth = useTuniAuthStrict()
  const sessionUsesTuniAuth = useSessionUsesTuniAuth()
  const isOptionalTuniAuth = authMode === 'optional'
  const usesTuniAuth = isStrictTuniAuth || sessionUsesTuniAuth

  /** Returns the best human-friendly display name for a user. */
  function getDisplayName(user: DisplayableUser): string {
    if (user.name) return user.name
    if (usesTuniAuth && user.email) return user.email
    return user.username
  }

  /** Returns the identifying string for a user (for admin tables, selects, etc.). */
  function getDisplayIdentifier(user: DisplayableUser): string {
    if (usesTuniAuth && user.email) return user.email
    return user.username
  }

  return {
    getDisplayName,
    getDisplayIdentifier,
    isTuniAuth: usesTuniAuth,
    isOptionalTuniAuth,
  }
}
